import { SourceType, SourceValidator, UrlParser } from "@authory-packages/core";
import { VerticalSpacing } from "../../VerticalSpacing";
import { V3TextInput } from "../../TextInput/V3TextInput";
import { V3BodyGrey35 } from "../../ReusableStyledComponents/V3BodyGrey35";
import { RightAlignPanel } from "../../ReusableStyledComponents/RightAlignPanel";
import { V3Button } from "../../Button";
import { V3Loader } from "../../Loader";
import { AddSourceFormik, getInputLabel, getInputPlaceholder, getInputUrlIcon, getUrlDescriptionText } from "./AddSource.utils";
import { INVALID_URLS, PodcastErrorMessage, YoutubeErrorMessage } from "../../../utils/sourceValidation";
import { V3ButtonColor } from "../../Button/V3Button.types";
import { useState } from "react";
import { V3CaptionGrey35 } from "../../ReusableStyledComponents/V3CaptionGrey35";
import { FormikProps } from "formik";
import { AddAliases } from "../../EditAliases/AddAliases";

interface AddSourceURLProps {
    formik: FormikProps<AddSourceFormik>;
    userName: string;
    hideBackButton?: boolean;
    onBackHandler: () => void;
    onSourceUrlHandler: (url: string) => Promise<void>;
    onGoToAddManual: () => void;
}

export const AddSourceURL = ({ formik, userName, hideBackButton, onBackHandler, onSourceUrlHandler, onGoToAddManual }: AddSourceURLProps) => {
    const [submitting, setSubmitting] = useState(false);

    let urlError = undefined;
    const urlHasError = formik.errors.hasOwnProperty("url") && formik.touched.hasOwnProperty("url");

    const isWebImport = formik.values.type === SourceType.Web;

    if (urlHasError) {
        if (formik.errors.url === INVALID_URLS.PODCAST) {
            urlError = <PodcastErrorMessage />;
        } else if (formik.errors.url === INVALID_URLS.YOUTUBE) {
            urlError = <YoutubeErrorMessage />;
        } else if (formik.errors.url === INVALID_URLS.SOURCE_MISSMATCH) {
            let type = SourceType.Web;

            try {
                const domain = UrlParser.extractDomain(formik.values.url);
                type = SourceValidator.getSourceTypeFromDomain(domain || "");
            } catch {}

            if (type === SourceType.Unsupported) type = SourceType.Web;

            urlError = (
                <span>
                    Did you mean to add a{" "}
                    <a
                        style={{ color: "inherit" }}
                        href=""
                        onClick={(e) => {
                            e.preventDefault();
                            formik.setFieldValue("type", type);
                        }}
                    >
                        {type}
                    </a>{" "}
                    source ?
                </span>
            );
        } else {
            urlError = formik.errors.url;
        }
    }

    return (
        <form onSubmit={formik.handleSubmit} action=".">
            <VerticalSpacing bottom={24}>
                <VerticalSpacing bottom={24}>
                    <V3CaptionGrey35>{getUrlDescriptionText(formik.values.type, onGoToAddManual)}</V3CaptionGrey35>
                </VerticalSpacing>
                <VerticalSpacing bottom={6}>
                    <V3BodyGrey35>
                        <strong>{getInputLabel(formik.values.type)}</strong>
                    </V3BodyGrey35>
                </VerticalSpacing>
                <V3TextInput
                    disableIconHover
                    name="url"
                    inputMode="url"
                    placeholder={getInputPlaceholder(formik.values.type)}
                    onChange={formik.handleChange}
                    icon={getInputUrlIcon(formik.values.type)}
                    onBlur={formik.handleBlur}
                    value={formik.values.url}
                    error={urlError}
                    autoFocus={false}
                    disabled={submitting}
                />
            </VerticalSpacing>
            {isWebImport && <AddAliases formik={formik} userName={userName} />}
            <RightAlignPanel>
                {!hideBackButton && !submitting && (
                    <div>
                        <V3Button
                            type="button"
                            text="Back"
                            color={V3ButtonColor.secondary}
                            onClick={(e) => {
                                e.preventDefault();
                                onBackHandler();
                            }}
                        />
                    </div>
                )}
                <div>
                    {submitting ? (
                        <V3Loader height={42} />
                    ) : (
                        <V3Button
                            text={"Continue"}
                            onClick={async () => {
                                setSubmitting(true);
                                try {
                                    await onSourceUrlHandler(formik.values.url);
                                } catch {}
                                setSubmitting(false);
                            }}
                        />
                    )}
                </div>
            </RightAlignPanel>
        </form>
    );
};
